import {
    faAward,
    faBinoculars,
    faCalendarDays,
    faCamera,
    faClock,
    faCompass,
    faFeather,
    faHouse,
    faLeaf,
    faLocationDot,
    faMapLocationDot,
    faTableCellsLarge,
    faTrophy
} from "@fortawesome/free-solid-svg-icons";
import type {HabitatMomentRule, JourneyMoment, LearningGroup} from "./types";

export const LIFE_JOURNEY_HERO_IMAGE = '/assets/life-journey-hero.png';
export const LIFE_JOURNEY_CACHE_KEY = 'lifeJourneyCache';
export const LIFE_JOURNEY_CACHE_VERSION = 2;

export const LIFE_SPECIES_MILESTONES = [10, 25, 50, 100, 200, 300, 500, 750, 1000, 1500, 2000, 3000, 4000, 5000, 7500, 10000];
export const MAX_JOURNEY_CARDS = 30;
export const MAX_JOURNEY_FACTLETS = 6;
export const CONSERVATION_MOMENT_CATEGORIES = ['NT', 'VU', 'EN', 'CR'];

const JOURNEY_ICONS = [
    faAward,
    faBinoculars,
    faCalendarDays,
    faCamera,
    faCompass,
    faFeather,
    faClock,
    faHouse,
    faLeaf,
    faLocationDot,
    faMapLocationDot,
    faTableCellsLarge,
    faTrophy
];

export const JOURNEY_ICON_BY_NAME = JOURNEY_ICONS.reduce((icons, icon) => {
    icons.set(icon.iconName, icon);
    return icons;
}, new Map<string, typeof faFeather>());

export const JOURNEY_CHAPTER_ORDER: JourneyMoment['chapter'][] = [
    'beginnings',
    'learning',
    'conservation',
    'growth',
    'places',
    'habitats',
    'bests',
    'habits',
    'rediscovery',
    'photography'
];

export const BIOME_MILESTONES = [5, 10, 14];
export const MAJOR_BIOME_COUNT = 14;

export const HABITAT_MOMENT_RULES: HabitatMomentRule[] = [
    {
        id: 'forest',
        title: 'Your first forest',
        detailLabel: 'Forest habitat',
        matches: {biomeIncludes: ['forest']}
    },
    {
        id: 'grassland',
        title: 'Your first grassland',
        detailLabel: 'Grassland habitat',
        matches: {biomeIncludes: ['grassland', 'savanna', 'shrubland']}
    },
    {
        id: 'wetland',
        title: 'Your first wetland',
        detailLabel: 'Wetland habitat',
        matches: {biomeIncludes: ['flooded grasslands', 'mangroves'], ecoregionIncludes: ['wetland', 'marsh', 'swamp']}
    },
    {
        id: 'desert',
        title: 'Your first desert',
        detailLabel: 'Desert habitat',
        matches: {biomeIncludes: ['desert', 'xeric']}
    },
    {
        id: 'alpine',
        title: 'Your first alpine habitat',
        detailLabel: 'Alpine habitat',
        matches: {biomeIncludes: ['montane grasslands', 'tundra'], ecoregionIncludes: ['alpine']}
    },
    {
        id: 'tropical-rainforest',
        title: 'Your first tropical rainforest',
        detailLabel: 'Tropical rainforest',
        matches: {biomeIncludes: ['tropical & subtropical moist broadleaf forests']}
    },
    {
        id: 'mangrove',
        title: 'Your first mangrove',
        detailLabel: 'Mangrove habitat',
        matches: {biomeIncludes: ['mangrove']}
    },
    {
        id: 'pelagic',
        title: 'Your first pelagic adventure',
        detailLabel: 'Open-water checklist',
        matches: {locationIncludes: ['pelagic', 'offshore', 'open ocean', 'ocean', 'at sea']}
    },
    {
        id: 'tundra',
        title: 'A walk through the tundra',
        detailLabel: 'Tundra habitat',
        matches: {biomeIncludes: ['tundra']}
    },
    {
        id: 'cloud-forest',
        title: 'Into the cloud forest',
        detailLabel: 'Cloud forest habitat',
        matches: {ecoregionIncludes: ['montane forests', 'cloud forest']}
    },
    {
        id: 'tropical-climate',
        title: 'Your first tropical climate',
        detailLabel: 'Tropical biome',
        matches: {biomeIncludes: ['tropical']}
    },
    {
        id: 'arid-landscape',
        title: 'Your first arid landscape',
        detailLabel: 'Arid landscape',
        matches: {biomeIncludes: ['desert', 'xeric']}
    },
    {
        id: 'boreal-forest',
        title: 'Your first boreal forest',
        detailLabel: 'Boreal forest',
        matches: {biomeIncludes: ['boreal forests', 'taiga']}
    },
    {
        id: 'mediterranean-ecosystem',
        title: 'Your first Mediterranean ecosystem',
        detailLabel: 'Mediterranean ecosystem',
        matches: {biomeIncludes: ['mediterranean']}
    }
];

export const LEARNING_GROUPS: LearningGroup[] = [
    {id: 'ducks', label: 'duck', familyMatches: ['ducks, geese, and waterfowl']},
    {id: 'raptors', label: 'raptor', familyMatches: ['hawks', 'eagles', 'kites', 'falcons', 'caracaras', 'osprey']},
    {id: 'gulls', label: 'gull', familyMatches: ['gulls']},
    {id: 'new-world-sparrows', label: 'New World sparrow', familyMatches: ['new world sparrows']},
    {id: 'old-world-sparrows', label: 'Old World sparrow', familyMatches: ['old world sparrows', 'weavers', 'waxbills']},
    {id: 'new-world-warblers', label: 'New World warbler', familyMatches: ['wood-warblers', 'wood warblers']},
    {id: 'old-world-warblers', label: 'Old World warbler', familyMatches: ['leaf warblers', 'bush warblers', 'reed warblers', 'grassbirds', 'cisticolas', 'sylviid warblers']},
    {id: 'owls', label: 'owl', familyMatches: ['owls']},
    {id: 'hummingbirds', label: 'hummingbird', familyMatches: ['hummingbirds']},
    {id: 'shorebirds', label: 'shorebird', familyMatches: ['sandpipers', 'plovers', 'oystercatchers', 'avocets', 'stilts']},
    {id: 'woodpeckers', label: 'woodpecker', familyMatches: ['woodpeckers']},
    {id: 'new-world-flycatchers', label: 'New World flycatcher', familyMatches: ['tyrant flycatchers']},
    {id: 'old-world-flycatchers', label: 'Old World flycatcher', familyMatches: ['old world flycatchers', 'chats']},
    {id: 'pigeons', label: 'pigeon', familyMatches: ['pigeons', 'doves']},
    {id: 'kingfishers', label: 'kingfisher', familyMatches: ['kingfishers']},
    {id: 'hornbills', label: 'hornbill', familyMatches: ['hornbills']},
    {id: 'toucans', label: 'toucan', familyMatches: ['toucans']},
    {id: 'parrots', label: 'parrot', familyMatches: ['parrots', 'parakeets', 'macaws', 'cockatoos']},
    {id: 'kinglets', label: 'kinglet', familyMatches: ['kinglets']},
    {id: 'tits', label: 'tit', familyMatches: ['tits', 'chickadees', 'titmice']},
    {id: 'nuthatches', label: 'nuthatch', familyMatches: ['nuthatches']},
    {id: 'creepers', label: 'creeper', familyMatches: ['treecreepers', 'creepers']},
    {id: 'swallows', label: 'swallow', familyMatches: ['swallows', 'martins']},
    {id: 'wagtails', label: 'wagtail', familyMatches: ['wagtails', 'pipits']},
    {id: 'finches', label: 'finch', familyMatches: ['finches']},
    {id: 'buntings', label: 'bunting', familyMatches: ['buntings']},
    {id: 'tanagers', label: 'tanager', familyMatches: ['tanagers']},
    {id: 'blackbirds', label: 'blackbird', familyMatches: ['new world blackbirds', 'blackbirds']},
    {id: 'starlings', label: 'starling', familyMatches: ['starlings']},
    {id: 'thrushes', label: 'thrush', familyMatches: ['thrushes']},
    {id: 'corvids', label: 'corvid', familyMatches: ['crows', 'jays', 'magpies', 'corvids']},
    {id: 'cuckoos', label: 'cuckoo', familyMatches: ['cuckoos']},
    {id: 'nightjars', label: 'nightjar', familyMatches: ['nightjars', 'nighthawks']},
    {id: 'swifts', label: 'swift', familyMatches: ['swifts']},
    {id: 'pigeons-doves', label: 'pigeon-dove', familyMatches: ['pigeons', 'doves']},
    {id: 'herons', label: 'heron', familyMatches: ['herons', 'egrets', 'bitterns']},
    {id: 'ibises', label: 'ibis', familyMatches: ['ibises', 'spoonbills']},
    {id: 'cranes', label: 'crane', familyMatches: ['cranes']},
    {id: 'rails', label: 'rail', familyMatches: ['rails', 'gallinules', 'coots']},
    {id: 'grebes', label: 'grebe', familyMatches: ['grebes']},
    {id: 'loons', label: 'loon', familyMatches: ['loons']},
    {id: 'alcids', label: 'alcid', familyMatches: ['auks', 'murres', 'puffins', 'alcids']},
    {id: 'terns', label: 'tern', familyMatches: ['terns']},
    {id: 'falcons', label: 'falcon', familyMatches: ['falcons', 'caracaras']},
    {id: 'vultures', label: 'vulture', familyMatches: ['new world vultures', 'old world vultures', 'vultures']}
];
